import { Col, Modal, Row, Form, Button as BsButton } from "react-bootstrap";
import { useState, useEffect } from "react";
import { AbandonedVehicleReviewStatus } from "./api/Network";
import { Api } from "./api/Api";
import { Button } from "./components/Button";
import { Match } from "./loaders/abandonedVehicleLoader";
import { AbandonedVehicle, AerialImage } from "./api/Application";
import { expandAbandonedVehicle } from "./loaders/abandonedVehicleCompressed";
import { useRevalidator } from "react-router-dom";
import { showInstantLocalDate, showInstantLocalDateTimeShort, showInstantLocalTime } from "./api/Date";
import { ImageBlendCanvas } from "./components/ImageBlendCanvas";
import { vehicleThumbnailAfter, vehicleThumbnailBefore } from "./api/Url";
import { showAbandonedVehicleReviewStatus } from "./api/EnumUtils";
import { clampTarget, getDialogPlacement, createVehiclePolygon, DialogPlacement, Vec3 } from "./common/Geometry";
import { RectSize } from "./common/Geography";
import { AbandonedVehicleReviewStatus as RS } from "./api/Network"
import { getReviewStatusIcon } from "./components/AbandonedVehicleStatusIconNew";
import React from "react";
import { areaCodes } from "./AbandonedVehicleListPageUtil";

export interface AbandonedVehicleModalProps {
    imageA:    AerialImage;
    imageB:    AerialImage;
    show:      AbandonedVehicle | undefined;
    setShow:   (value: AbandonedVehicle | undefined) => void;
    save:      (vehicle: Match) => void;
    paneSize:  RectSize;
}

const reviewStatuses = [
    AbandonedVehicleReviewStatus.New,         
    AbandonedVehicleReviewStatus.NotActionable,
    AbandonedVehicleReviewStatus.Actionable,  
] as const;

export function AbandonedVehicleReviewDialog( { imageA, imageB, show, setShow, save, paneSize }: AbandonedVehicleModalProps ) {
    const { revalidate } = useRevalidator();
    const vehicle      = show;
    const latestStatus = vehicle?.reviewStatus?.status || AbandonedVehicleReviewStatus.New;
    const [status,   setStatus]   = useState<AbandonedVehicleReviewStatus>( latestStatus );
    const [note,     setNote]     = useState( vehicle?.reviewStatus?.note ?? "" );
    const [isSaving, setIsSaving] = useState(false);

    useEffect( () => {
        if( vehicle ) {
            setStatus( vehicle.reviewStatus?.status || AbandonedVehicleReviewStatus.New );
            setNote( vehicle.reviewStatus?.note ?? "" );
        }
    }, [vehicle] );

    if( !vehicle ) { 
        return <></>;
    }
    const handleClose = () => {
        setShow( undefined );
        setNote( "" );
        setStatus( AbandonedVehicleReviewStatus.New );
    };

    const handleSave = async () => {
        setIsSaving( true );
        try {
            const result = await Api.abandonedVehicleReviewUpdate( vehicle.abandonedVehicleId, { note, status } );
            if( result.isOk ) {
                save( { ...expandAbandonedVehicle( result.value ) } );
                revalidate(); //trigger the loader to run
                setShow( undefined );
            } else {
                alert( "Failed to update: " + result.error );
            }
        } catch {
            alert("Error updating vehicle status");
        } finally {
            setIsSaving(false);
        }
    };
    const address   = vehicle.attributes?.address?.split(",")[0] ?? "Unknown";
    const isDirty   = status !== latestStatus || note.length > 0;

    const maxBounds = [imageB.width, imageB.height] as RectSize;
    const target    = [vehicle.x, vehicle.y, 0] as Vec3;
    const zoom      = 2;
    const scale     = 2 ** zoom;
    const clamped = clampTarget( target, zoom, paneSize, maxBounds );
    let { offset } = getDialogPlacement( target, clamped, zoom );
    let [ offsetX, offsetY ] = offset;

    const pts  = createVehiclePolygon( vehicle );
    const xs   = pts.map( p => p[0] );
    const ys   = pts.map( p => p[1] );

    const centerX = ( Math.min( ...xs ) + Math.max( ...xs ) ) / 2;
    const topY    = Math.min( ...ys );

    const TIP_MARGIN = 0;
    const biasPx = paneSize[1] * ( 3/5 - 1/2 ); // = paneSize[1] / 10
    offsetX += ( centerX - vehicle.x ) * scale;
    offsetY += ( topY    - vehicle.y ) * scale - TIP_MARGIN + biasPx;

    const iconRec = vehicle.reviewStatus?.status !== undefined 
        ? getReviewStatusIcon( vehicle.reviewStatus?.status ) : undefined;

    return (
            <div className="custom-modal-dialog"
                style={{ "--dlg-x": `${offsetX}px`, "--dlg-y": `${offsetY}px` } as React.CSSProperties}>            
                <div className="custom-modal-content av-panel bg-body rounded">
                <Modal.Body className="p-3">
                    <div className="text-monospace d-flex justify-content-between align-items-center mb-0">
                        <div>
                            <h5 className="d-flex align-items-center gap-1 mb-1">
                                {address}
                            </h5>
                            <div className="gap-1" style={{
                                display: "grid",
                                gridTemplateColumns: "auto auto auto auto",
                                width: "fit-content" }}>
                                <span className="small font-monospace text-body-secondary">Seen</span>
                                <span className="small font-monospace">{showInstantLocalDateTimeShort( imageA.capturedWhen! )}</span>
                                <span className="small font-monospace text-body-secondary">at</span>
                                <span className="small font-monospace">{showInstantLocalTime( imageA.capturedWhen! )}</span>
                                
                                <span className="small font-monospace text-body-secondary text-end"></span>
                                <span className="small font-monospace">{showInstantLocalDateTimeShort( imageB.capturedWhen! )}</span>
                                <span className="small font-monospace text-body-secondary">at</span>
                                <span className="small font-monospace">{showInstantLocalTime( imageB.capturedWhen! )}</span>

                                {/* <span className="d-none d-xl-inline text-success">Reviewed</span> */}
                                {/* <span>{showInstantLocalDate( vehicle.status.createdWhen! )}</span>
                                <span className="text-body-secondary">at</span>
                                <span>{showInstantLocalTime( vehicle.status.createdWhen! )}</span> */}
                            </div>
                        </div>
                        <div className="position-relative d-flex justify-content-end">
                            {iconRec !== undefined && <div className="position-absolute" style={{ zIndex: 1, top: -12, left: -10 }}>
                                {React.cloneElement( iconRec.icon, { className: `${iconRec.color} ${iconRec.size}` } )}
                            </div>}
                            <ImageBlendCanvas
                                className="rounded"
                                imageA={vehicleThumbnailBefore( vehicle.abandonedVehicleId )}
                                imageB={vehicleThumbnailAfter( vehicle.abandonedVehicleId )}
                                width={128} height={128} />
                        </div>
                    </div>                    
                    <div className="my-3"></div>
                    {/* Review section */}
                    <Row>
                        <Col xs={12}>
                            <div className="d-flex gap-2 mb-3">
                                <BsButton key={RS.New}
                                    variant={status === RS.New ? `info` : `outline-info`}
                                    className="py-2"
                                    style={{ flex: "1 1 0", minWidth: 0 }}
                                    onClick={ () => setStatus( RS.New ) }>
                                    {showAbandonedVehicleReviewStatus( RS.New )}
                                </BsButton>
                                <BsButton key={RS.NotActionable}
                                    variant={status === RS.NotActionable ? `success` : `outline-success`}
                                    className="py-2"
                                    style={{ flex: "1 1 0", minWidth: 0 }}
                                    onClick={ () => setStatus( RS.NotActionable ) }>
                                    {showAbandonedVehicleReviewStatus( RS.NotActionable )}
                                </BsButton>
                                <BsButton key={RS.Actionable}
                                    variant={status === RS.Actionable ? `danger` : `outline-danger`}
                                    className="py-2"
                                    style={{ flex: "1 1 0", minWidth: 0 }}
                                    onClick={ () => setStatus( RS.Actionable ) }>
                                    {showAbandonedVehicleReviewStatus( RS.Actionable )}
                                </BsButton>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <Form.Group>
                                <Form.Control value={note}
                                    onChange={e => setNote( e.target.value )}
                                    placeholder="Notes..." />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex gap-2 border-top-0 px-4 pb-4 pt-0">
                    <Button className="flex-grow-1" variant="outline-secondary" onClick={handleClose}>Close</Button>
                    <Button className="flex-grow-1" variant="primary" onClick={handleSave} disabled={isSaving || !isDirty}>
                        Submit
                    </Button>
                </Modal.Footer>
            </div>
        </div>
    );
}